import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Bell, Home, Menu } from 'lucide-react';

const VISITS_KEY = 'erp_nav_visits_v1';
const MAX_TRACKED = 40;

function readVisits(userKey) {
  try { return JSON.parse(localStorage.getItem(`${VISITS_KEY}:${userKey}`)) || {}; } catch { return {}; }
}

function writeVisits(userKey, visits) {
  // Keep the map small: a long-lived login visits dozens of screens, and only
  // the top few ever reach the bar.
  const trimmed = Object.fromEntries(
    Object.entries(visits).sort((a, b) => b[1] - a[1]).slice(0, MAX_TRACKED),
  );
  try { localStorage.setItem(`${VISITS_KEY}:${userKey}`, JSON.stringify(trimmed)); } catch { /* ignore */ }
}

/**
 * The phone's tab bar: Home, the two screens this person opens most, Alerts and
 * Menu.
 *
 * The two middle slots are learned per login rather than configured per role.
 * A procurement clerk and an owner open different screens, and so do two clerks
 * on different shifts; a per-role list somebody has to maintain is wrong for
 * both the day it is written and the day a new screen is added. The sidebar
 * (behind Menu) still lists everything — the bar is a shortcut, never the only
 * way to a screen. Hidden from md up, where the sidebar or the rail is on screen.
 */
export default function MobileBottomNav({
  items, isItemActive, iconMap, userKey, onMenu, menuOpen,
  notificationsVisible, unreadNotifications,
}) {
  const location = useLocation();
  const [visits, setVisits] = useState(() => readVisits(userKey));

  useEffect(() => { setVisits(readVisits(userKey)); }, [userKey]);

  // Count a visit against the nav item that owns the current route, so a detail
  // page (/sales/123) counts towards Sales rather than becoming a slot of its own.
  // `items` is a fresh array on every Layout render, so the effect re-runs often;
  // the ref makes sure one arrival counts once. It still waits for `items`,
  // because on a cold load the nav is empty until the feature flags arrive.
  const countedRef = useRef(null);
  useEffect(() => {
    const visitKey = `${userKey}|${location.pathname}`;
    if (countedRef.current === visitKey) return;
    const owner = items.find((i) => isItemActive(i));
    if (!owner) return;
    countedRef.current = visitKey;
    setVisits((prev) => {
      const next = { ...prev, [owner.path]: (prev[owner.path] || 0) + 1 };
      writeVisits(userKey, next);
      return next;
    });
    // isItemActive is rebuilt on every Layout render; the route is what matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, items, userKey]);

  const home = items.find((i) => i.pin === 'top') || items[0];

  const favourites = useMemo(() => {
    const excluded = new Set([home?.path, '/notifications']);
    const ranked = items
      .filter((i) => !excluded.has(i.path) && visits[i.path])
      .sort((a, b) => (visits[b.path] || 0) - (visits[a.path] || 0));
    // A brand-new login has no history yet; fall back to the first screens the
    // sidebar offers them, which are already filtered to their role.
    const fill = items.filter((i) => !excluded.has(i.path) && !ranked.includes(i));
    return [...ranked, ...fill].slice(0, notificationsVisible ? 2 : 3);
  }, [items, visits, home, notificationsVisible]);

  const tab = (key, to, label, Icon, active, badge) => (
    <Link
      key={key}
      to={to}
      className={`relative flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 px-1 py-1.5 text-[10.5px] font-medium ${
        active ? 'text-blue-700' : 'text-slate-500'
      }`}
      aria-current={active ? 'page' : undefined}
    >
      <span className={`flex h-7 w-12 items-center justify-center rounded-full ${active ? 'bg-blue-50' : ''}`}>
        {Icon ? <Icon size={20} strokeWidth={active ? 2.4 : 2} /> : null}
      </span>
      <span className="max-w-full truncate">{label}</span>
      {badge ? (
        <span className="absolute right-[22%] top-1 min-w-[16px] rounded-full bg-rose-500 px-1 text-center text-[9px] font-bold leading-4 text-white">
          {badge > 99 ? '99+' : badge}
        </span>
      ) : null}
    </Link>
  );

  return (
    <nav
      className="mobile-bottom-nav fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 backdrop-blur md:hidden print:hidden"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      aria-label="Quick navigation"
    >
      <div className="flex h-16 items-stretch">
        {home && tab('home', home.path, 'Home', Home, isItemActive(home))}
        {favourites.map((i) => tab(i.path, i.path, i.name, iconMap[i.icon], isItemActive(i)))}
        {notificationsVisible && tab(
          'alerts', '/notifications', 'Alerts', Bell,
          location.pathname.startsWith('/notifications'), unreadNotifications,
        )}
        <button
          type="button"
          onClick={onMenu}
          className={`flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 px-1 py-1.5 text-[10.5px] font-medium ${
            menuOpen ? 'text-blue-700' : 'text-slate-500'
          }`}
          aria-label="Open menu"
          data-testid="mobile-bottom-menu"
        >
          <span className={`flex h-7 w-12 items-center justify-center rounded-full ${menuOpen ? 'bg-blue-50' : ''}`}>
            <Menu size={20} />
          </span>
          <span>Menu</span>
        </button>
      </div>
    </nav>
  );
}
